import type { TFunction } from 'i18next'
import type { FieldErrors, FieldValues } from 'react-hook-form'

/**
 * Form error UX helpers (contract + POC forms). Presentation only: they never
 * change what is validated, only how a failure is worded and where the page
 * looks after a failed submit.
 */

// yup's default messages start with the field path, e.g.
// `order.products[1].service_price_register must be greater than or equal to 1`.
// Only snake_case / dotted / indexed paths are treated as paths, so a normal
// English sentence ("Password must be ...") is left alone.
const PATH =
    '([A-Za-z0-9_]+(?:\\[\\d+\\])*(?:\\.[A-Za-z0-9_]+(?:\\[\\d+\\])*)*)'
const looksLikePath = (p: string) => /[_.[]/.test(p)

const RULES: {
    re: RegExp
    key: string
    vars?: (m: RegExpMatchArray) => object
}[] = [
    {
        re: new RegExp(`^${PATH} is a required field$`),
        key: 'form-errors.required',
    },
    {
        re: new RegExp(`^${PATH} must be greater than or equal to (.+)$`),
        key: 'form-errors.min',
        vars: (m) => ({ n: m[2] }),
    },
    {
        re: new RegExp(`^${PATH} must be greater than (.+)$`),
        key: 'form-errors.more-than',
        vars: (m) => ({ n: m[2] }),
    },
    {
        re: new RegExp(`^${PATH} must be less than or equal to (.+)$`),
        key: 'form-errors.max',
        vars: (m) => ({ n: m[2] }),
    },
    {
        re: new RegExp(`^${PATH} must be less than (.+)$`),
        key: 'form-errors.less-than',
        vars: (m) => ({ n: m[2] }),
    },
    {
        re: new RegExp(`^${PATH} must be a valid email$`),
        key: 'form-errors.email',
    },
    {
        re: new RegExp(`^${PATH} must be a \`number\` type`),
        key: 'form-errors.number',
    },
    {
        re: new RegExp(`^${PATH} must be a \`date\` type`),
        key: 'form-errors.date',
    },
    {
        re: new RegExp(`^${PATH} field must have at least (\\d+) items?`),
        key: 'form-errors.min-items',
        vars: (m) => ({ n: m[2] }),
    },
    {
        re: new RegExp(`^${PATH} (?:must|field) .*$`),
        key: 'form-errors.invalid',
    },
    {
        re: new RegExp(`^${PATH} is invalid$`),
        key: 'form-errors.invalid',
    },
]

/** Last path segment without index: `a.b[0].price_per_unit` -> `price_per_unit`. */
const leafOf = (path: string) =>
    path
        .split('.')
        .pop()!
        .replace(/\[\d+\]/g, '')

/**
 * Turns a raw yup default message into a Vietnamese/English sentence naming the
 * field by the label the user sees. Any other message goes through `t` exactly
 * as before (i18n keys and already-written sentences are unchanged).
 */
export function translateFormError(message: string, t: TFunction): string {
    for (const rule of RULES) {
        const m = message.match(rule.re)
        if (!m || !looksLikePath(m[1])) continue
        const leaf = leafOf(m[1])
        const label =
            t(`form-errors.field.${leaf}`, { defaultValue: '' }) ||
            t('form-errors.field.unknown')
        return t(rule.key, { label, ...(rule.vars?.(m) ?? {}) })
    }
    return t(message)
}

type Leaf = { path: string; message?: string }

function collect(node: unknown, path: string, out: Leaf[]) {
    if (!node || typeof node !== 'object') return
    const n = node as Record<string, unknown>
    if (typeof n.message === 'string' && 'type' in n) {
        out.push({ path, message: n.message })
        return
    }
    for (const [k, v] of Object.entries(n)) {
        if (k === 'ref' || k === 'root') continue
        collect(v, path ? `${path}.${k}` : k, out)
    }
}

/** Every invalid leaf of a react-hook-form error tree, in tree order. */
function listInvalid<T extends FieldValues>(errors: FieldErrors<T>) {
    const out: Leaf[] = []
    collect(errors, '', out)
    return out
}

/**
 * Number of invalid items for the summary toast: one per invalid product block
 * (`order.products[i]`), plus one per other invalid field.
 */
export function countInvalidItems<T extends FieldValues>(
    errors: FieldErrors<T>
) {
    const seen = new Set<string>()
    for (const { path } of listInvalid(errors)) {
        const block = path.match(/^order\.products\.(\d+)/)
        seen.add(block ? `block-${block[1]}` : path)
    }
    return seen.size
}

/**
 * Scrolls to (and focuses) the first visible field marked invalid. Retries for
 * a few frames: a collapsed block the caller just re-opened is not laid out in
 * the same frame as the error state.
 */
export function focusFirstInvalid(tries = 12) {
    const el = Array.from(
        document.querySelectorAll<HTMLElement>(
            '[aria-invalid="true"], [data-form-error="true"]'
        )
    ).find((e) => e.getClientRects().length > 0)
    if (!el) {
        if (tries > 0) requestAnimationFrame(() => focusFirstInvalid(tries - 1))
        return
    }
    el.scrollIntoView({ block: 'center', behavior: 'smooth' })
    try {
        el.focus({ preventScroll: true })
    } catch {
        /* not focusable: the scroll is enough */
    }
}
